import { DeleteOutlined, EyeOutlined, SearchOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Empty, Form, Input, Modal, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  useBulkCreateReportBuilderScheduleMutation,
  useDeleteReportBuilderMutation,
  useRecipients,
  useReportBuilderHomeList,
  useSaveReportBuilderGroupMutation,
} from '../../hooks/useReports'
import { getSelectedUtcTime } from '../../features/reports/schedule/time'
import { useAuthorization } from '../../contexts/AuthorizationContext'
import { useAuthSession } from '../../lib/session'
import { permissions, UserTypes } from '../../types/authorization'
import type { Recipient, ReportBuilderGroupItem, ReportBuilderListItem } from '../../types/reports'

type GroupedReportRow = ReportBuilderListItem & {
  key: string
  isGroup?: boolean
  reportBuilderGroupId?: string
  groupName?: string
  children?: GroupedReportRow[]
}

type ScheduleFormValues = {
  schedule: string
  frequency?: string
  hour: string
  minute: string
  period: 'AM' | 'PM'
  fileType: string
  subject?: string
  body?: string
  userIds?: string[]
  groupIds?: string[]
  ccIds?: string[]
}

const SCHEDULE_OPTIONS = [
  { value: 'D', label: 'Daily' },
  { value: 'W', label: 'Weekly' },
  { value: 'M', label: 'Monthly' },
  { value: 'NOW', label: 'Now' },
]

const WEEKLY_OPTIONS = [
  { value: 'Mon', label: 'Monday' },
  { value: 'Tue', label: 'Tuesday' },
  { value: 'Wed', label: 'Wednesday' },
  { value: 'Thu', label: 'Thursday' },
  { value: 'Fri', label: 'Friday' },
  { value: 'Sat', label: 'Saturday' },
  { value: 'Sun', label: 'Sunday' },
]

const MONTHLY_OPTIONS = Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))
const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const value = String(i + 1).padStart(2, '0')
  return { value, label: value }
})
const MINUTE_OPTIONS = ['00', '15', '30', '45'].map((value) => ({ value, label: value }))
const PERIOD_OPTIONS = [
  { value: 'AM', label: 'AM' },
  { value: 'PM', label: 'PM' },
]
const DEFAULT_FILE_TYPE = 'xlsx'

export default function ReportsCreatedPage() {
  const session = useAuthSession()
  const { hasAnyPermission, hasPermission } = useAuthorization()
  const isDiAdmin = session?.currentUserTypeId === UserTypes.Admin
  const canDeleteReports = hasPermission(permissions.updateNotification)
  const canCreateOrEditReports = hasAnyPermission([
    permissions.addReport,
    permissions.editReport,
    permissions.updateNotification,
  ])
  const canManageSchedules = hasAnyPermission([
    permissions.addscheduledreport,
    permissions.editschedulereport,
    permissions.updateNotification,
  ])
  const canModifyCreatedReports = canCreateOrEditReports || canManageSchedules
  const navigate = useNavigate()
  const location = useLocation()
  const clientId = session?.currentClientId || ''

  const [titleFilter, setTitleFilter] = useState('')
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const [groupModalOpen, setGroupModalOpen] = useState(false)
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false)
  const [editingGroup, setEditingGroup] = useState<ReportBuilderGroupItem | null>(null)
  const [editingGroupReportIds, setEditingGroupReportIds] = useState<string[]>([])
  const [editingGroupName, setEditingGroupName] = useState('')
  const [scheduleForm] = Form.useForm<ScheduleFormValues>()

  const reportsQuery = useReportBuilderHomeList(clientId, false)
  const recipientsQuery = useRecipients(clientId)
  const saveGroupMutation = useSaveReportBuilderGroupMutation()
  const bulkScheduleMutation = useBulkCreateReportBuilderScheduleMutation()
  const deleteReportMutation = useDeleteReportBuilderMutation()
  const scheduleType = Form.useWatch('schedule', scheduleForm)

  const groupedRows = useMemo<GroupedReportRow[]>(() => {
    const groups = reportsQuery.data || []
    const filter = titleFilter.trim().toLowerCase()

    const mapped = groups
      .map((group, index) => {
        const children = (group.reportList || [])
          .filter((report) => (filter ? report.title.toLowerCase().includes(filter) : true))
          .map((report) => ({
            ...report,
            key: report.reportBuilderId,
          }))

        if (!children.length) return null

        const groupName = (group.groupName || '').trim() || 'Ungrouped'
        return {
          key: `group:${group.reportBuilderGroupId || groupName}:${index}`,
          reportBuilderId: '',
          reportBuilderGroupId: group.reportBuilderGroupId,
          groupName,
          title: groupName,
          description: '',
          isTemplate: false,
          count: group.count || children.reduce((sum, row) => sum + (row.count || 0), 0),
          recipients: [],
          platformType: group.platformType,
          isGroup: true,
          children,
        } as GroupedReportRow
      })
      .filter((row): row is GroupedReportRow => Boolean(row))

    const grouped = mapped.filter((row) => row.title !== 'Ungrouped')
    const ungroupedChildren = mapped.filter((row) => row.title === 'Ungrouped').flatMap((row) => row.children || [])

    if (!ungroupedChildren.length) return grouped

    return [
      ...grouped,
      {
        key: 'group:ungrouped',
        reportBuilderId: '',
        title: 'Ungrouped',
        groupName: 'Ungrouped',
        description: '',
        isTemplate: false,
        count: ungroupedChildren.reduce((sum, row) => sum + (row.count || 0), 0),
        recipients: [],
        isGroup: true,
        children: ungroupedChildren,
      },
    ]
  }, [reportsQuery.data, titleFilter])

  const reportLookup = useMemo(() => {
    const lookup = new Map<string, GroupedReportRow>()
    groupedRows.forEach((group) => {
      ;(group.children || []).forEach((child) => {
        lookup.set(child.reportBuilderId, child)
      })
    })
    return lookup
  }, [groupedRows])

  const groupLookup = useMemo(() => {
    const lookup = new Map<string, GroupedReportRow>()
    groupedRows.forEach((row) => {
      if (row.isGroup) lookup.set(row.key, row)
    })
    return lookup
  }, [groupedRows])

  const groupByIdLookup = useMemo(() => {
    const lookup = new Map<string, ReportBuilderGroupItem>()
    ;(reportsQuery.data || []).forEach((group) => {
      if (group.reportBuilderGroupId) {
        lookup.set(group.reportBuilderGroupId, group)
      }
    })
    return lookup
  }, [reportsQuery.data])

  const selectedReportIds = useMemo(
    () => selectedRowKeys.filter((id) => reportLookup.has(id)),
    [reportLookup, selectedRowKeys]
  )

  const selectedGroupKeys = useMemo(
    () => selectedRowKeys.filter((id) => groupLookup.has(id)),
    [groupLookup, selectedRowKeys]
  )

  const selectedGroups = useMemo(
    () => selectedGroupKeys.map((key) => groupLookup.get(key)).filter((row): row is GroupedReportRow => Boolean(row)),
    [groupLookup, selectedGroupKeys]
  )

  const selectedReports = useMemo(
    () => selectedReportIds.map((id) => reportLookup.get(id)).filter((row): row is GroupedReportRow => Boolean(row)),
    [reportLookup, selectedReportIds]
  )

  const selectedPlatformTypes = useMemo(() => {
    const types = new Set<number>()
    selectedGroups.forEach((row) => {
      if (row.platformType) types.add(row.platformType)
    })
    selectedReports.forEach((row) => {
      if (row.platformType) types.add(row.platformType)
    })
    return types
  }, [selectedGroups, selectedReports])

  const hasMixedSelectionTypes = selectedGroups.length > 0 && selectedReports.length > 0

  const selectionSummary = useMemo(() => {
    if (selectedGroups.length) {
      return {
        isGroup: true,
        rows: selectedGroups
          .filter((row) => row.reportBuilderGroupId)
          .map((row) => ({
            value: String(row.reportBuilderGroupId),
            name: row.title,
          })),
      }
    }
    return {
      isGroup: false,
      rows: selectedReports.map((row) => ({
        value: row.reportBuilderId,
        name: row.title,
      })),
    }
  }, [selectedGroups, selectedReports])

  const groupedReportIds = useMemo(() => {
    const ids = new Set<string>()
    ;(reportsQuery.data || []).forEach((group) => {
      if (!group.reportBuilderGroupId) return
      ;(group.reportList || []).forEach((report) => {
        ids.add(report.reportBuilderId)
      })
    })
    return ids
  }, [reportsQuery.data])

  const recipientOptions = useMemo(() => {
    const all = recipientsQuery.data || []
    return {
      users: all.filter((item) => item.type === 1),
      groups: all.filter((item) => item.type === 2),
    }
  }, [recipientsQuery.data])

  const groupModalOptions = useMemo(() => {
    const allReports = (reportsQuery.data || []).flatMap((group) => group.reportList || [])
    const excludedGroupedIds = new Set<string>()

    ;(reportsQuery.data || []).forEach((group) => {
      if (!group.reportBuilderGroupId || group.reportBuilderGroupId === editingGroup?.reportBuilderGroupId) return
      ;(group.reportList || []).forEach((report) => excludedGroupedIds.add(report.reportBuilderId))
    })

    return allReports
      .filter((report) => {
        if (editingGroup?.platformType && report.platformType !== editingGroup.platformType) return false
        if (excludedGroupedIds.has(report.reportBuilderId)) return false
        return true
      })
      .map((report) => ({
        value: report.reportBuilderId,
        label: report.title,
      }))
  }, [editingGroup, reportsQuery.data])

  useEffect(() => {
    setSelectedRowKeys((current) => current.filter((key) => reportLookup.has(key) || groupLookup.has(key)))
  }, [groupLookup, reportLookup])

  function resetGroupModal() {
    setEditingGroup(null)
    setEditingGroupName('')
    setEditingGroupReportIds([])
  }

  function openCreateGroupModal() {
    if (selectedGroups.length > 0) {
      message.error('A group cannot be added to another group.')
      return
    }
    if (selectedPlatformTypes.size > 1) {
      message.error('Please select reports from only one platform type.')
      return
    }
    if (selectedReportIds.length < 2) {
      message.error('Please select at least two reports to group.')
      return
    }
    const invalidReport = selectedReports.find((report) => groupedReportIds.has(report.reportBuilderId))
    if (invalidReport) {
      message.error(`Report "${invalidReport.title}" is already assigned to a group.`)
      return
    }

    resetGroupModal()
    setEditingGroupReportIds(selectedReportIds)
    setGroupModalOpen(true)
  }

  const openEditGroupModal = useCallback((groupId: string) => {
    const group = groupByIdLookup.get(groupId)
    if (!group) return
    setEditingGroup(group)
    setEditingGroupName(String(group.groupName || ''))
    setEditingGroupReportIds((group.reportList || []).map((report) => report.reportBuilderId))
    setGroupModalOpen(true)
  }, [groupByIdLookup])

  async function submitGroupSelection() {
    if (!clientId) return
    const name = editingGroupName.trim()
    if (!name) {
      message.error('Group name is required.')
      return
    }
    if (editingGroupReportIds.length < 2) {
      message.error('Please select at least two reports to group.')
      return
    }

    try {
      await saveGroupMutation.mutateAsync({
        clientId,
        reportBuilderIdList: editingGroupReportIds,
        reportBuilderGroupId: editingGroup?.reportBuilderGroupId || '00000000-0000-0000-0000-000000000000',
        groupName: name,
      })
      message.success('Report group saved successfully.')
      setGroupModalOpen(false)
      resetGroupModal()
      setSelectedRowKeys([])
      await reportsQuery.refetch()
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to save report group.')
    }
  }

  async function ungroupSelectedReports() {
    if (!clientId || selectedReportIds.length === 0) return

    try {
      await saveGroupMutation.mutateAsync({
        clientId,
        reportBuilderIdList: selectedReportIds,
        reportBuilderGroupId: '',
        groupName: '',
      })
      message.success('Reports moved to Ungrouped.')
      setSelectedRowKeys([])
      await reportsQuery.refetch()
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to ungroup selected reports.')
    }
  }

  function openScheduleModal() {
    if (!selectionSummary.rows.length) {
      message.error('Please select at least one report or report group to schedule.')
      return
    }
    if (hasMixedSelectionTypes) {
      message.error('Please select only reports or only groups when creating schedules.')
      return
    }
    if (selectedPlatformTypes.size > 1) {
      message.error('Please select rows from only one platform type.')
      return
    }

    scheduleForm.resetFields()
    scheduleForm.setFieldsValue({
      schedule: 'D',
      hour: '08',
      minute: '00',
      period: 'AM',
      fileType: DEFAULT_FILE_TYPE,
      userIds: [],
      groupIds: [],
      ccIds: [],
      subject: '',
      body: '',
    })
    setScheduleModalOpen(true)
  }

  async function submitScheduleSelection() {
    if (!clientId || !selectionSummary.rows.length) return

    try {
      const values = await scheduleForm.validateFields()
      const recipientIds = [...(values.userIds || []), ...(values.groupIds || [])]
      if (!recipientIds.length) {
        throw new Error('Select at least one recipient from either the user or user groups list.')
      }

      const scheduleCode =
        values.schedule === 'W' || values.schedule === 'M' ? `${values.schedule}-${values.frequency || ''}` : values.schedule

      const recipients: Recipient[] = [...recipientOptions.users, ...recipientOptions.groups].filter((item) =>
        recipientIds.includes(item.id)
      )

      const ok = await bulkScheduleMutation.mutateAsync({
        reportBuilderScheduleBulkCreate: {
          idList: selectionSummary.rows.map((row) => row.value),
          isGroup: selectionSummary.isGroup,
          clientId,
          schedule: scheduleCode,
          scheduleTime: getSelectedUtcTime(values.hour, values.minute, values.period),
          fileType: DEFAULT_FILE_TYPE,
          cc: values.ccIds || [],
          subject: String(values.subject || ''),
          body: String(values.body || ''),
          recipients: recipients.map((item) => ({ id: item.id, type: item.type })),
        },
      })

      if (!ok) {
        throw new Error('Error with scheduling. Please try again.')
      }

      message.success('Scheduled successfully.')
      setScheduleModalOpen(false)
      setSelectedRowKeys([])
      await reportsQuery.refetch()
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Complete all schedule fields.')
    }
  }

  const confirmDeleteReport = useCallback((report: GroupedReportRow) => {
    Modal.confirm({
      title: 'Delete report',
      content: `Are you sure you want to delete "${report.title}"?`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        const ok = await deleteReportMutation.mutateAsync(report.reportBuilderId)
        if (!ok) {
          message.error('Failed to delete report.')
          return
        }
        message.success('Report deleted.')
        setSelectedRowKeys((current) => current.filter((key) => key !== report.reportBuilderId))
      },
    })
  }, [deleteReportMutation])

  const columns = useMemo<ColumnsType<GroupedReportRow>>(
    () => [
      {
        title: 'Report Name',
        dataIndex: 'title',
        key: 'title',
        sorter: (a, b) => a.title.localeCompare(b.title),
        render: (_, row) =>
          row.isGroup && row.reportBuilderGroupId && canModifyCreatedReports ? (
            <Button type="link" className="px-0" onClick={() => openEditGroupModal(String(row.reportBuilderGroupId))}>
              <Typography.Text strong>{row.title}</Typography.Text>
            </Button>
          ) : row.isGroup ? (
            <Typography.Text strong>{row.title}</Typography.Text>
          ) : canCreateOrEditReports ? (
            <Button type="link" className="px-0" onClick={() => navigate(`/reports/custom/${row.reportBuilderId}`)}>
              {row.title}
            </Button>
          ) : (
            <Typography.Text>{row.title}</Typography.Text>
          ),
      },
      {
        title: '# of Schedules',
        dataIndex: 'count',
        key: 'count',
        width: 180,
        render: (count: number, row) =>
          row.isGroup ? (
            <span>-</span>
          ) : count > 0 && canManageSchedules ? (
            <Button type="link" className="px-0" onClick={() => navigate(`/reports/schedules/${row.reportBuilderId}`)}>
              {count}
            </Button>
          ) : (
            <span>{count > 0 ? count : 'None'}</span>
          ),
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        render: (value: string, row) => (row.isGroup ? '-' : value || '-'),
      },
      {
        title: 'Type',
        key: 'isTemplate',
        width: 140,
        render: (_, row) =>
          row.isGroup ? '-' : <Tag color={row.isTemplate ? 'gold' : 'blue'}>{row.isTemplate ? 'Template' : 'Custom'}</Tag>,
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 110,
        render: (_, row) => {
          if (!row.isGroup) {
            return (
              <Space size="small">
                <Tooltip title="View">
                  <Button
                    type="text"
                    size="small"
                    icon={<EyeOutlined />}
                    aria-label={`View ${row.title}`}
                    onClick={() => navigate(`/reports/workspace/${row.reportBuilderId}`)}
                  />
                </Tooltip>
                {canDeleteReports ? (
                  <Tooltip title="Delete">
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      aria-label={`Delete ${row.title}`}
                      onClick={() => confirmDeleteReport(row)}
                    />
                  </Tooltip>
                ) : null}
              </Space>
            )
          }
          if (row.reportBuilderGroupId) {
            return (
              <Tooltip title="View">
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  aria-label={`View ${row.title}`}
                  onClick={() => navigate(`/reports/workspace/group/${row.reportBuilderGroupId}`)}
                />
              </Tooltip>
            )
          }
          return null
        },
      },
    ],
    [canCreateOrEditReports, canDeleteReports, canManageSchedules, canModifyCreatedReports, confirmDeleteReport, navigate, openEditGroupModal]
  )

  return (
    <Card>
      <Space direction="vertical" size={16} className="w-full">
        <Space className="w-full justify-between" align="start">
          <div>
            <Typography.Title level={3} className="!mb-1">
              PracIntel Created Reports
            </Typography.Title>
            <Typography.Text type="secondary">Current client: {session?.currentClientName || 'Unknown'}</Typography.Text>
          </div>
          <Space wrap>
            <Button onClick={() => navigate(-1)}>Back</Button>
            <Button type={location.pathname === '/reports/created' ? 'primary' : 'default'} onClick={() => navigate('/reports/created')}>
              Created
            </Button>
            <Button type={location.pathname === '/reports/templates' ? 'primary' : 'default'} onClick={() => navigate('/reports/templates')}>
              Templates
            </Button>
            {isDiAdmin ? (
              <Button onClick={() => navigate('/reports/options')}>
                Options
              </Button>
            ) : null}
            {canCreateOrEditReports ? <Button onClick={() => navigate('/reports/custom/new')}>+ New Report</Button> : null}
          </Space>
        </Space>

        {!clientId ? <Alert type="warning" showIcon message="No active client selected." /> : null}

        <Space className="w-full justify-between" wrap>
          <Input
            value={titleFilter}
            onChange={(event) => setTitleFilter(event.target.value)}
            placeholder="Search by report title"
            suffix={<SearchOutlined />}
            allowClear
            style={{ width: 320 }}
            aria-label="Search created reports"
          />
          <Space wrap>
            {selectedRowKeys.length > 0 ? <Typography.Text type="secondary">{selectedRowKeys.length} selected</Typography.Text> : null}
            {canManageSchedules ? (
              <Button disabled={selectedRowKeys.length === 0 || !clientId} onClick={openScheduleModal}>
                Schedule
              </Button>
            ) : null}
            {canCreateOrEditReports ? (
              <>
                <Button disabled={selectedRowKeys.length === 0 || !clientId} onClick={openCreateGroupModal}>
                  Group
                </Button>
                <Button disabled={selectedReportIds.length === 0 || !clientId} onClick={ungroupSelectedReports}>
                  Move to Ungrouped
                </Button>
              </>
            ) : null}
            {reportsQuery.isFetching && !reportsQuery.isLoading ? <Typography.Text type="secondary">Refreshing...</Typography.Text> : null}
          </Space>
        </Space>

        {reportsQuery.isError ? (
          <Alert
            type="error"
            showIcon
            message="Failed to load created reports"
            description={reportsQuery.error instanceof Error ? reportsQuery.error.message : 'Unknown error'}
            action={
              <Button size="small" onClick={() => reportsQuery.refetch()}>
                Retry
              </Button>
            }
          />
        ) : null}

        {!reportsQuery.isError && !reportsQuery.isLoading && groupedRows.length === 0 ? <Empty description="No created reports found" /> : null}

        <Table<GroupedReportRow>
          rowKey={(row) => (row.isGroup ? row.key : row.reportBuilderId)}
          loading={reportsQuery.isLoading}
          columns={columns}
          dataSource={groupedRows}
          expandable={{ defaultExpandAllRows: true }}
          rowSelection={{
            selectedRowKeys,
            checkStrictly: true,
            onChange: (keys) => setSelectedRowKeys(keys.map((key) => String(key))),
          }}
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
        />
      </Space>

      <Modal
        title={editingGroup ? 'Edit Report Group' : 'Create Report Group'}
        open={groupModalOpen}
        onCancel={() => {
          setGroupModalOpen(false)
          resetGroupModal()
        }}
        onOk={submitGroupSelection}
        okText="Save"
        confirmLoading={saveGroupMutation.isPending}
      >
        <Space direction="vertical" className="w-full" size={12}>
          <Input value={editingGroupName} onChange={(event) => setEditingGroupName(event.target.value)} placeholder="Group name" maxLength={120} />
          <Select
            mode="multiple"
            value={editingGroupReportIds}
            onChange={(value) => setEditingGroupReportIds(value)}
            options={groupModalOptions}
            placeholder="Select reports"
            showSearch
            optionFilterProp="label"
          />
          <Typography.Text type="secondary">Select at least two reports. Existing grouped reports outside this group are excluded.</Typography.Text>
        </Space>
      </Modal>

      <Modal
        title={`Schedule ${selectionSummary.isGroup ? 'Groups' : 'Reports'}`}
        open={scheduleModalOpen}
        onCancel={() => setScheduleModalOpen(false)}
        onOk={() => void submitScheduleSelection()}
        okText="Save"
        confirmLoading={bulkScheduleMutation.isPending}
        width={820}
      >
        <Form<ScheduleFormValues>
          form={scheduleForm}
          layout="vertical"
          initialValues={{ schedule: 'D', hour: '08', minute: '00', period: 'AM', fileType: DEFAULT_FILE_TYPE }}
        >
          <Form.Item label={`${selectionSummary.isGroup ? 'Groups' : 'Reports'} Selected`}>
            <Select mode="multiple" open={false} value={selectionSummary.rows.map((row) => row.name)} options={[]} disabled />
          </Form.Item>

          <Space className="w-full" wrap>
            <Form.Item name="schedule" label="Schedule" rules={[{ required: true, message: 'Schedule is required' }]}>
              <Select style={{ width: 170 }} options={SCHEDULE_OPTIONS} />
            </Form.Item>
            {scheduleType === 'W' ? (
              <Form.Item name="frequency" label="Day of Week" rules={[{ required: true, message: 'Frequency is required' }]}>
                <Select style={{ width: 170 }} options={WEEKLY_OPTIONS} />
              </Form.Item>
            ) : null}
            {scheduleType === 'M' ? (
              <Form.Item name="frequency" label="Day of Month" rules={[{ required: true, message: 'Frequency is required' }]}>
                <Select style={{ width: 170 }} options={MONTHLY_OPTIONS} />
              </Form.Item>
            ) : null}
            {scheduleType !== 'NOW' ? (
              <>
                <Form.Item name="hour" label="Hour" rules={[{ required: true, message: 'Hour is required' }]}>
                  <Select style={{ width: 110 }} options={HOUR_OPTIONS} />
                </Form.Item>
                <Form.Item name="minute" label="Minute" rules={[{ required: true, message: 'Minute is required' }]}>
                  <Select style={{ width: 110 }} options={MINUTE_OPTIONS} />
                </Form.Item>
                <Form.Item name="period" label="Period" rules={[{ required: true, message: 'Period is required' }]}>
                  <Select style={{ width: 110 }} options={PERIOD_OPTIONS} />
                </Form.Item>
              </>
            ) : null}
          </Space>

          <Form.Item name="userIds" label="Users">
            <Select mode="multiple" options={recipientOptions.users.map((item) => ({ value: item.id, label: item.name }))} />
          </Form.Item>
          <Form.Item name="groupIds" label="User Groups">
            <Select mode="multiple" options={recipientOptions.groups.map((item) => ({ value: item.id, label: item.name }))} />
          </Form.Item>
          <Form.Item name="ccIds" label="Cc">
            <Select mode="multiple" options={recipientOptions.users.map((item) => ({ value: item.id, label: item.name }))} />
          </Form.Item>
          <Form.Item name="subject" label="Subject">
            <Input />
          </Form.Item>
          <Form.Item name="body" label="Body">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  )
}
